<template>
  <DocLayout v-if="!isLoading && thread" :doc="doc" :show-text-controls="false">
    <template #page-content>
      <div class="thread book-reading__content-editor">
        <h2 class="thread__title">{{ thread.title }}</h2>

        <p>
          <span class="thread-user text-gray">
            <div class="thread-comment__name">
              <div class="thread-comment__icon-wrapper">
                <span class="iconfont iprofile-icon"></span>
              </div>
              <span class="thread-comment__username">{{
                thread.author.username
              }}</span>
              <span class="thread-comment__time">{{ thread.posted_time }}</span>
            </div>
          </span>
        </p>

        <p class="thread__text">{{ thread.text }}</p>
        <div v-if="isAuthor" class="thread-toolbar">
          <router-link
            class="btns btns--white btns--small"
            :to="{
              name: doc.routeNames().edit_thread,
              state: { threadUrlSlug: thread.url_slug },
            }"
          >
            Edit
          </router-link>
        </div>
      </div>
      <ToggleButton
        class="discussion-toggle"
        :value="thread.notify_new_comment"
        @update:value="updateToggle"
      >
        Notify me of new comments in this discussion
      </ToggleButton>

      <div class="article-single__content-editor comment-box">
        <div class="comment-wrapper">
          <a @click.stop.prevent="focusReply"
            ><i class="iconfont icategory-communication"></i> Comment</a
          >
          <CommentEdit ref="commentEdit" :doc="doc" :thread="thread" />
        </div>
        <Comments :doc="doc" :thread="thread" />
      </div>

      <Footer />
    </template>
  </DocLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import DocLayout from '@/app/components/doc/Layout.vue'
import Footer from '@/app/components/Footer.vue'
import Comments from '@/app/components/community/Comments.vue'
import CommentEdit from '@/app/components/community/CommentEdit.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'

import { backend } from '@/services/backend'
import { auth } from '@/services/auth'
import { Sentry } from '@/services/sentry'
import { ThreadView } from '@/models/forum'
import type { DocView } from '@/models/doc'
import { assertNotNull } from '@/helpers/typing'

const props = defineProps<{
  doc: DocView
}>()

const route = useRoute()
const router = useRouter()

const commentEdit = ref<InstanceType<typeof CommentEdit> | null>(null)

const isLoading = ref(false)
const thread = ref<Raw<ThreadView> | null>(null)

onBeforeMount(async () => {
  loadThread()
})

async function loadThread(): Promise<void> {
  try {
    isLoading.value = true
    const response = await backend.getThread(route.params.thread as string)
    thread.value = new ThreadView(response, props.doc)
    isLoading.value = false
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      Sentry.captureException(error)
      throw error
    }
  }
}

const isAuthor = computed(() => {
  return !!thread.value && thread.value.author.id === auth.getUserId()
})

function focusReply(): void {
  commentEdit.value?.focusInput()
}
async function updateToggle(value: boolean): Promise<void> {
  await assertNotNull(thread.value).toggleNotifyNewComment(value)
}
</script>
